/**
 * Supervisory ratios read off a BankState at a quarter-end (09.1 §4 inputs; 08.1 §7 ratios). One home for every metric
 * `ContentParams.supervisory.thresholds` names, so a threshold row resolves by key and never by a typed value.
 */
import { InvariantViolation, LOAN_BOOKS, totalAssetsUsd, WHOLESALE_BOOKS, type BankState, type BookId, type QuarterRecord } from "@bsim/domain";

/** Quarter records behind the trailing-four-quarter sums (08.2, 09.1 D4 and NI4, 09.3 ERI). */
const TRAILING_QUARTERS = 4;

const balanceUsd = (bank: BankState, books: readonly BookId[]): number => {
  let t = 0;
  for (const b of books) for (const v of bank.books[b].vintages) t += v.balanceUsd;
  return t;
};

/** Tangible equity: total equity (AOCI included) less goodwill and CDI (I13's TBVPS numerator). */
export function tangibleEquityUsd(bank: BankState): number {
  const e = bank.equity;
  return e.commonApicUsd + e.retainedEarningsUsd + e.aociAfsUsd + e.aociSwapUsd - bank.staticItemsUsd.goodwill - bank.staticItemsUsd.intangibles_cdi;
}

/** Tangible equity ÷ total assets (09.3 E1 critical test). */
export const tangibleEquityRate = (bank: BankState): number => tangibleEquityUsd(bank) / totalAssetsUsd(bank);

/** The last four quarter records' NI and distributions (dividends + buybacks), in dollars. */
export function trailingFourQuarters(bank: BankState): { readonly netIncomeUsd: number; readonly distributionsUsd: number; readonly records: readonly QuarterRecord[] } {
  const records = bank.quarters.slice(-TRAILING_QUARTERS);
  let netIncomeUsd = 0, distributionsUsd = 0;
  for (const q of records) { netIncomeUsd += q.netIncomeUsd; distributionsUsd += q.dividendsUsd + q.buybacksUsd; }
  return { netIncomeUsd, distributionsUsd, records };
}

/** 08.1 §7 ratios: reported CET1, Tier 1 and total risk-based capital over RWA; leverage over the quarter-average leverage assets. */
export function capitalRatios(bank: BankState): { readonly cet1Rate: number; readonly tier1Rate: number; readonly trbcRate: number; readonly leverageRate: number } {
  const c = bank.capital;
  return { cet1Rate: c.cet1Usd / c.rwaUsd, tier1Rate: c.tier1Usd / c.rwaUsd, trbcRate: c.trbcUsd / c.rwaUsd, leverageRate: c.tier1Usd / c.avgAssetsForLeverageUsd };
}

/** 05.5 E3 / 09.1 §4: the income a bank loses in the worse of the ±100bp moves, as a positive share of a year's NII: max(0, −S_worse); the +100bp figure stands where no worse side was computed. */
export const niiSensitivityLossRate = (irr: Pick<BankState["irr"], "niiSensitivityRate" | "niiSensitivityWorseRate">): number => Math.max(0, -(irr.niiSensitivityWorseRate ?? irr.niiSensitivityRate));

/** The 09.1 §4 metrics, keyed as `ContentParams.supervisory.thresholds[*].metric` names them. */
export function camelsMetricValues(bank: BankState): Readonly<Record<string, number>> {
  const { capital: c } = bank;
  const grossLoansUsd = balanceUsd(bank, LOAN_BOOKS);
  const assetsUsd = totalAssetsUsd(bank);
  let nonaccrualUsd = 0;
  for (const b of LOAN_BOOKS) nonaccrualUsd += bank.credit?.[b]?.nonaccrualUsd ?? 0;
  const t4 = trailingFourQuarters(bank);
  let ncoUsd = 0;
  for (const q of t4.records) ncoUsd += q.ncoUsd ?? 0;
  return {
    ...capitalRatios(bank),
    nplToCet1PlusAclRate: nonaccrualUsd / (c.cet1Usd + bank.aclUsd),
    ncoRate4q: ncoUsd / grossLoansUsd,
    roa4q: t4.netIncomeUsd / assetsUsd,
    lcrLiteRate: bank.liquidity.lcrLiteRate,
    slcRate: bank.liquidity.slcRate,
    wholesaleShareRate: balanceUsd(bank, WHOLESALE_BOOKS) / assetsUsd,
    eve200LossRate: Math.max(0, -bank.irr.eve200Rate),
    niiSensitivityLossRate: niiSensitivityLossRate(bank.irr),
    markedGapShareRate: 1 - c.markedCet1Usd / c.cet1Usd,
  };
}

/** A threshold row's metric value; a metric this module does not compute is a spec gap, never a default. */
export function metricValue(metrics: Readonly<Record<string, number>>, name: string): number {
  const v = metrics[name];
  if (v === undefined) throw new InvariantViolation("CAMELS_METRIC", `no producer for supervisory metric "${name}"`);
  return v;
}

